<template>
  <div
    v-if="component.frequentlyAskedQuestions.length > 0"
    data-testid="faq-section"
    class="relative isolate w-full"
  >
    <strapi-background-image
      :image="component.backgroundImage"
      :repeat="RepeatType.Y"
    />
    <div
      data-testid="faq-wrapper"
      class="max-w-screen-xl mx-auto px-4 relative z-1 py-24"
    >
      <h2 class="mb-5 uppercase">{{ component.title }}</h2>
      <div
        class="grid grid-cols-1 lg:grid-cols-2 gap-x-6 items-start self-start"
      >
        <div
          v-for="(question, index) in component.frequentlyAskedQuestions"
          :key="question.id"
          ref="faqCols"
          style="margin-top: var(--faq-grid-negative-margin)"
        >
          <strapi-faq-item
            class="mb-5"
            :content="question.content"
            :title="question.title"
            :icon-url="question.icon"
            :is-opened="index === openAnswerIndex"
            @toggle-answer="toggleAnswer(index)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { StrapiFaqList, StrapiProductFaq } from '~/apollo/types/types';
import { RepeatType } from '~/components/strapi/types';

defineProps<{
  component: StrapiProductFaq | StrapiFaqList;
}>();

const faqCols = ref<Array<HTMLDivElement>>([]);
const { lg } = useAppBreakpoints();

const openAnswerIndex = ref(-1);

const toggleAnswer = async (index: number) => {
  const isClosing = openAnswerIndex.value === index;

  if (isClosing) {
    openAnswerIndex.value = -1;
  } else {
    openAnswerIndex.value = index;
  }

  if (lg.value) {
    const targetCol = faqCols.value[index];
    const beforeHeight = targetCol.getBoundingClientRect().height;

    await nextTick();

    const afterHeight = targetCol.getBoundingClientRect().height;

    if (isClosing) {
      faqCols.value.forEach((faqCard) => {
        faqCard.style.setProperty('--faq-grid-negative-margin', '0px');
      });
    } else {
      const targetIsInColumn = index % 2 === 0;
      const startingIndex = targetIsInColumn ? index + 2 : index;

      faqCols.value.forEach((faqCard, faqCardIndex) => {
        const cardIsInOtherColumn = targetIsInColumn
          ? faqCardIndex % 2 !== 0
          : faqCardIndex % 2 === 0;

        if (faqCardIndex > startingIndex && cardIsInOtherColumn) {
          faqCard.style.setProperty(
            '--faq-grid-negative-margin',
            `-${afterHeight - beforeHeight}px`,
          );
        } else {
          faqCard.style.setProperty('--faq-grid-negative-margin', '0px');
        }
      });
    }
  }
};

watch(
  () => lg.value,
  () => {
    openAnswerIndex.value = -1;
    faqCols.value.forEach((card) => {
      card.style.setProperty('--faq-grid-negative-margin', '0px');
    });
  },
);
</script>
